import React, { useCallback, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import TimelineIcon from '@mui/icons-material/Timeline'
import PaymentsIcon from '@mui/icons-material/Payments'
import InsightsIcon from '@mui/icons-material/Insights'
import EventIcon from '@mui/icons-material/Event'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import API from '../../services/API'
import PageHeader from '../kit/PageHeader'
import SectionCard from '../kit/SectionCard'
import StatTile from '../kit/StatTile'
import { SoftChip } from '../kit/Chips'
import { formatCurrency, formatPeriod, stageMeta } from '../kit/helpers'

// The 12-month revenue forecast grid (mirrors the Excel sheet it replaces):
// customers and open leads as rows, months as columns, grouped by sales
// stage with per-group and combined totals. Figures derive from contract /
// expected-subscription fees; any cell can be clicked and overridden
// (step-ups, one-offs) — an override persists until cleared, and clearing a
// cell restores the derived figure. Totals are per currency and never summed
// across currencies.

const headCellSx = {
  color: '#64748B',
  fontWeight: 700,
  fontSize: '0.72rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  bgcolor: '#F8FAFC',
  borderColor: '#EEF2F7',
  whiteSpace: 'nowrap',
}

const cellSx = {
  borderColor: '#EEF2F7',
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
}

const stickySx = {
  position: 'sticky',
  left: 0,
  zIndex: 2,
  bgcolor: '#fff',
  borderRight: '1px solid #EEF2F7',
  minWidth: 190,
  maxWidth: 240,
}

const FLAG_COPY = {
  noFees: 'No fees recorded — nothing to project for this row',
  noRenewalDate:
    'Annual fee with no renewal date on the contract — months left empty',
  cancelling: 'Subscription is cancelling; projection stops at the cancellation date',
}

// '£1,234' for the main currency, with any other currencies appended
// ('£1,234 · €500'). Never adds amounts across currencies.
const renderMoneyMap = (byCurrency = {}) => {
  const entries = Object.entries(byCurrency).filter(([, amount]) => amount > 0)
  if (!entries.length) return '—'
  entries.sort(([a], [b]) => (a === 'GBP' ? -1 : b === 'GBP' ? 1 : a.localeCompare(b)))
  return entries.map(([ccy, amount]) => formatCurrency(amount, ccy)).join(' · ')
}

const effectiveAmount = (cell) => cell?.override ?? cell?.derived ?? 0

const Forecast = () => {
  const navigate = useNavigate()
  const [start, setStart] = useState(() => moment().format('YYYY-MM'))
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  // { rowKey, month, value } while a cell is being edited
  const [editing, setEditing] = useState(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(
    async (quiet = false) => {
      if (!quiet) setLoading(true)
      const res = await API.get('/admincrm/forecast', { params: { start } })
      if (res.status === 200 && res.data?.months) setData(res.data)
      else setError(res.data?.message || 'Could not load the forecast.')
      setLoading(false)
    },
    [start],
  )

  useEffect(() => {
    load()
  }, [load])

  const months = data?.months ?? []

  // Headline tiles, per currency. Pipeline = every non-customer group.
  const headline = useMemo(() => {
    if (!data) return null
    const customers = data.groups.find((group) => group.stage === 'Converted')
    const pipeline = {}
    for (const group of data.groups) {
      if (group.stage === 'Converted') continue
      for (const [ccy, amount] of Object.entries(group.totals.year ?? {})) {
        pipeline[ccy] = (pipeline[ccy] ?? 0) + amount
      }
    }
    return {
      year: data.totals.year ?? {},
      thisMonth: data.totals.months?.[months[0]] ?? {},
      customers: customers?.totals.year ?? {},
      pipeline,
    }
  }, [data, months])

  const rowKey = (row) => `${row.subjectType}:${row.subjectId}`

  const openCell = (row, month) => {
    if (saving) return
    const cell = row.cells[month]
    setEditing({
      rowKey: rowKey(row),
      subjectType: row.subjectType,
      subjectId: row.subjectId,
      month,
      value:
        cell?.override != null
          ? String(cell.override)
          : cell?.derived != null
            ? String(cell.derived)
            : '',
      derived: cell?.derived ?? null,
    })
  }

  // Empty input = clear the override (back to derived). A number = override.
  // Saving the exact derived value also clears, so cells do not get stuck on
  // a stale copy of the derivation.
  const commitEdit = async () => {
    const edit = editing
    if (!edit) return
    setEditing(null)
    const raw = edit.value.trim()
    const amount = raw === '' ? null : Number(raw)
    if (raw !== '' && (!Number.isFinite(amount) || amount < 0)) {
      setError('Override must be a positive number (or empty to reset).')
      return
    }
    const clear = amount === null || amount === edit.derived
    const current = data
    const cell = current?.groups
      ?.flatMap((group) => group.rows)
      ?.find((row) => rowKey(row) === edit.rowKey)?.cells?.[edit.month]
    const unchanged = clear ? cell?.override == null : cell?.override === amount
    if (unchanged) return

    setSaving(true)
    const payload = {
      subjectType: edit.subjectType,
      subjectId: edit.subjectId,
      month: edit.month,
    }
    const res = clear
      ? await API.delete('/admincrm/forecast/override', { data: payload })
      : await API.put('/admincrm/forecast/override', { ...payload, amount })
    if (res.status !== 200) {
      setError(res.data?.message || 'Could not save the override.')
      setSaving(false)
      return
    }
    // Totals move too, so re-fetch quietly rather than patching locally.
    await load(true)
    setSaving(false)
  }

  const openRow = (row) => {
    if (row.tenantId) navigate(`/tenants/${row.tenantId}`)
    else if (row.leadId) navigate(`/sales/${row.leadId}`)
  }

  const shiftStart = (monthsDelta) =>
    setStart((prev) => moment(prev, 'YYYY-MM').add(monthsDelta, 'months').format('YYYY-MM'))

  const totalRowSx = { '& td': { ...cellSx, fontWeight: 800, bgcolor: '#F8FAFC' } }

  return (
    <Box>
      <PageHeader
        eyebrow="Sales"
        title="Forecast"
        subtitle="Projected revenue for the next 12 months — contract and expected fees, with hand-tuned overrides"
        actions={
          <Stack direction="row" alignItems="center" gap={0.5}>
            <IconButton size="small" onClick={() => shiftStart(-1)} aria-label="Earlier month">
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography variant="subtitle2" sx={{ minWidth: 150, textAlign: 'center' }}>
              {formatPeriod(start)} – {months.length ? formatPeriod(months[months.length - 1]) : ''}
            </Typography>
            <IconButton size="small" onClick={() => shiftStart(1)} aria-label="Later month">
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Stack>
        }
      />

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {(loading || saving) && <LinearProgress sx={{ mb: 2 }} />}

      {headline && (
        <Grid container spacing={1.5} mb={2}>
          <Grid item xs={6} md={3}>
            <StatTile
              label="12-month total"
              value={renderMoneyMap(headline.year)}
              icon={<TimelineIcon />}
              tone="#239AA4"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatTile
              label={`${months.length ? formatPeriod(months[0]) : 'This month'}`}
              value={renderMoneyMap(headline.thisMonth)}
              icon={<EventIcon />}
              tone="#268EBB"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatTile
              label="Customers (12m)"
              value={renderMoneyMap(headline.customers)}
              icon={<PaymentsIcon />}
              tone="#00964C"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatTile
              label="Pipeline (12m)"
              value={renderMoneyMap(headline.pipeline)}
              icon={<InsightsIcon />}
              tone="#85276A"
            />
          </Grid>
        </Grid>
      )}

      {data && (
        <SectionCard
          dense
          title="Forecast grid"
          subtitle="Click a cell to override it (step-ups, one-offs); clear the cell to restore the derived figure. Overridden cells are tinted."
        >
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1400, '& th, & td': { px: 1.25 } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, ...stickySx, zIndex: 3 }}>
                    Organisation
                  </TableCell>
                  {months.map((ym) => (
                    <TableCell key={ym} align="right" sx={headCellSx}>
                      {formatPeriod(ym, 'MMM YY')}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ ...headCellSx, fontWeight: 800 }}>
                    Year total
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.groups.map((group) => {
                  const meta = stageMeta(group.stage)
                  return (
                    <React.Fragment key={group.stage}>
                      <TableRow>
                        <TableCell
                          colSpan={months.length + 2}
                          sx={{ ...cellSx, bgcolor: `${meta.color}14`, py: 0.75 }}
                        >
                          <SoftChip color={meta.color} label={`${meta.label}s`} />
                        </TableCell>
                      </TableRow>
                      {group.rows.map((row) => (
                        <TableRow key={rowKey(row)} hover>
                          <TableCell sx={{ ...cellSx, ...stickySx }}>
                            <Stack direction="row" alignItems="center" gap={0.5} minWidth={0}>
                              <Link
                                component="button"
                                type="button"
                                variant="body2"
                                onClick={() => openRow(row)}
                                sx={{
                                  fontWeight: 600,
                                  textAlign: 'left',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {row.name}
                              </Link>
                              {row.flags?.filter((flag) => FLAG_COPY[flag]).map((flag) => (
                                <Tooltip key={flag} title={FLAG_COPY[flag]}>
                                  <WarningAmberRoundedIcon
                                    sx={{ fontSize: 16, color: '#F28B1F' }}
                                  />
                                </Tooltip>
                              ))}
                              {row.currency !== 'GBP' && (
                                <SoftChip color="#557A95" label={row.currency} />
                              )}
                            </Stack>
                          </TableCell>
                          {months.map((ym) => {
                            const cell = row.cells[ym]
                            const isEditing =
                              editing &&
                              editing.rowKey === rowKey(row) &&
                              editing.month === ym
                            if (isEditing) {
                              return (
                                <TableCell key={ym} align="right" sx={{ ...cellSx, p: 0.25 }}>
                                  <TextField
                                    autoFocus
                                    size="small"
                                    type="number"
                                    value={editing.value}
                                    onChange={(event) =>
                                      setEditing((prev) => ({ ...prev, value: event.target.value }))
                                    }
                                    onBlur={commitEdit}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter') commitEdit()
                                      if (event.key === 'Escape') setEditing(null)
                                    }}
                                    inputProps={{
                                      min: 0,
                                      style: { textAlign: 'right', padding: '4px 6px', width: 80 },
                                    }}
                                  />
                                </TableCell>
                              )
                            }
                            const amount = effectiveAmount(cell)
                            const overridden = cell?.override != null
                            return (
                              <TableCell
                                key={ym}
                                align="right"
                                onClick={() => openCell(row, ym)}
                                sx={{
                                  ...cellSx,
                                  cursor: 'pointer',
                                  ...(overridden ? { bgcolor: '#FFF7ED', fontWeight: 700 } : {}),
                                  '&:hover': { bgcolor: overridden ? '#FFEFD9' : '#F1F5F9' },
                                }}
                              >
                                {overridden ? (
                                  <Tooltip
                                    title={`Manual override${
                                      cell.derived != null
                                        ? ` (derived ${formatCurrency(cell.derived, row.currency)})`
                                        : ''
                                    } — clear the cell to restore`}
                                  >
                                    <span>{formatCurrency(amount, row.currency)}</span>
                                  </Tooltip>
                                ) : amount > 0 ? (
                                  formatCurrency(amount, row.currency)
                                ) : (
                                  '—'
                                )}
                              </TableCell>
                            )
                          })}
                          <TableCell align="right" sx={{ ...cellSx, fontWeight: 700 }}>
                            {row.yearTotal > 0
                              ? formatCurrency(row.yearTotal, row.currency)
                              : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={totalRowSx}>
                        <TableCell sx={{ ...cellSx, ...stickySx, fontWeight: 800, bgcolor: '#F8FAFC' }}>
                          {meta.label}s total
                        </TableCell>
                        {months.map((ym) => (
                          <TableCell key={ym} align="right">
                            {renderMoneyMap(group.totals.months[ym])}
                          </TableCell>
                        ))}
                        <TableCell align="right">{renderMoneyMap(group.totals.year)}</TableCell>
                      </TableRow>
                    </React.Fragment>
                  )
                })}
                <TableRow sx={totalRowSx}>
                  <TableCell sx={{ ...cellSx, ...stickySx, fontWeight: 800, bgcolor: '#F8FAFC' }}>
                    Combined total
                  </TableCell>
                  {months.map((ym) => (
                    <TableCell key={ym} align="right">
                      {renderMoneyMap(data.totals.months[ym])}
                    </TableCell>
                  ))}
                  <TableCell align="right">{renderMoneyMap(data.totals.year)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>
      )}
    </Box>
  )
}

export default Forecast
